import styled from "@emotion/styled";
import {
  Paper,
  Theme,
  Typography,
  Icon,
  useTheme,
  Tag,
  Button,
  defaultDateFormat,
  Tooltip,
  Loading,
  Skeleton,
} from "@yuno/design-system-web";
import { useVerifyPermission } from "@yuno/dashboard-container-mfe";
import { PermissionsAvailable } from "@yuno/dashboard-authentication-mfe";
import { queryClient } from "@yuno/dashboard-api-mfe";
import { useContext, useEffect, useLayoutEffect, useState } from "react";
import { MainContext } from "../../context/main-context";
import { DialogPublish } from "../dialog-publish";
import moment from "moment";
import { useTranslation } from "react-i18next";

const Container = styled(Paper)(({ theme }: { theme?: Theme }) => ({
  padding: 10,
  borderBottom: `1px solid ${theme.palette.grey[2]}`,
}));

const InfoHeaderText = styled(Typography)(({ theme }: { theme?: Theme }) => ({
  color: `${theme.palette.grey[5]}`,
  fontSize: `${theme.typography.tiny.regular.fontSize}`,
  paddingLeft: "16px",
}));

const InfoHeaderWrapper = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
}));

const Info = styled("div")(() => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
}));

const ButtonStyled = styled(Button)(() => ({
  width: "102px",
}));

const ButtonStyledTry = styled(Button)(({ theme }: { theme?: Theme }) => ({
  width: "102px",
  marginRight: "15px",
}));

export const Header = () => {
  const { t } = useTranslation();
  const [showText, setShowText] = useState(true);
  const contextValue = useContext(MainContext);
  const { allowed } = useVerifyPermission(
    PermissionsAvailable.BUILD_YOUR_CHECKOUT
  );

  const {
    checkout,
    isLoadingCheckout,
    handlePublish,
    lists,
    statusMutateCheckout,
    refetch,
    userAction,
    setUserAction,
  } = contextValue;

  const theme: Theme = useTheme();
  const [open, setOpen] = useState(false);
  const userProfile = queryClient.getQueryState(["fetch-profile"]);

  const handleToggleDialog = () => setOpen((prev) => !prev);

  const handleTry = () => window.open("https://demo.y.uno/", "_blank");

  useEffect(() => {
    setUserAction(false);
  }, [isLoadingCheckout]);

  useLayoutEffect(() => {
    if (statusMutateCheckout === "loading") setShowText(false);

    if (statusMutateCheckout === "success") {
      refetch();
    }
  }, [statusMutateCheckout]);

  return (
    <Container>
      <DialogPublish
        open={open}
        onPublish={handlePublish}
        onToggle={handleToggleDialog}
      />
      <InfoHeaderWrapper>
        {!isLoadingCheckout ? (
          <Info>
            {!checkout.published_at ? (
              <>
                <Icon
                  name="CloudSlash"
                  size="16px"
                  color={theme.palette.grey[5]}
                />
                <InfoHeaderText>{t('notPublished')}</InfoHeaderText>
              </>
            ) : (
              <>
                <Tag
                  label={t('published')}
                  type="success"
                  iconColor="success"
                  icon="CheckCircle"
                />
                <InfoHeaderText>
                  {t('lastPublished')}{" "}
                  {defaultDateFormat(
                    moment(checkout.published_at).toString(),
                    "MMM D, yyyy, hh:mm a",
                    true,
                    userProfile?.data?.settings?.timezone
                  )}
                </InfoHeaderText>
              </>
            )}
          </Info>
        ) : (
          <Info>
            <Skeleton variant="rectangular" width={200} height={20} />
          </Info>
        )}
        <ButtonStyledTry
          disabled={
            !checkout.published_at ||
            userAction ||
            statusMutateCheckout === "loading"
          }
          variant="tertiary"
          nonce={null}
          size="small"
          onClick={handleTry}
        >
          {t('try')}
        </ButtonStyledTry>
        <Tooltip
          disableHoverListener={allowed}
          title={t('permissions')}
          color="default"
          arrow
        >
          <span>
            <ButtonStyled
              disabled={
                (lists.enrollment?.length === 0 &&
                  lists.paymentMethod?.length === 0) ||
                statusMutateCheckout === "loading" ||
                !allowed
              }
              nonce={null}
              size="small"
              onClick={handleToggleDialog}
            >
              {statusMutateCheckout === "loading" ? <Loading /> : t('publish')}
            </ButtonStyled>
          </span>
        </Tooltip>
      </InfoHeaderWrapper>
    </Container>
  );
};
